import { useMemo, useState } from "react";
import { Button, NativeSelect } from "@altimateai/lego";
import { ArrowLeftIcon, ArrowRightIcon } from "@/assets/icons";
import styles from "./styles.module.scss";
import { Stack } from "@altimateai/lego";
import { Input } from "@altimateai/lego";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "./constants";

type ShowComponentProps = {
  pageSize: number;
  onPageSizeChange: (v: number) => void;
  isLoading?: boolean;
};

const ShowComponent = ({ pageSize, onPageSizeChange }: ShowComponentProps) => {
  const [pageSizeData, setPageSizeData] = useState(pageSize);

  return (
    <Stack align="center" aria-label="Results per page">
      <div>Show</div>
      <NativeSelect
        id={"paginate-dropdown"}
        onChange={(e: string) => {
          setPageSizeData(parseInt(e));
          onPageSizeChange(parseInt(e));
        }}
        value={pageSizeData.toString()}
        options={PAGE_SIZE_OPTIONS}
        size={"sm"}
        variant={"outline"}
        buttonProps={{ "aria-label": "Select page size" }}
      />

      <div>results</div>
    </Stack>
  );
};

type IndefinitePaginateProps = {
  page: number;
  onPageClick: (v: number) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  pageKeys: Record<string, string>;
} & ShowComponentProps;

const IndefinitePaginate = ({
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
  onPageClick,
  onPrevPage,
  onNextPage,
  pageKeys,
}: IndefinitePaginateProps) => {
  return (
    <div
      className="mt-4 al-flex al-justify-between al-items-center"
      aria-label="Pagination"
      role="navigation"
    >
      <ShowComponent pageSize={pageSize} onPageSizeChange={onPageSizeChange} />
      <div className="al-flex al-items-center">
        {page >= 1 && (
          <Button
            className={styles.arrowButtonLeft}
            onClick={e => {
              e.preventDefault();
              onPrevPage();
            }}
          >
            <ArrowLeftIcon style={{ width: "18px", height: "18px" }} />
          </Button>
        )}
        {Object.keys(pageKeys).map(p => (
          <Button
            color={page === parseInt(p) ? "primary" : "white"}
            key={p}
            className="ms-1 me-1"
            onClick={e => {
              e.preventDefault();
              onPageClick(parseInt(p));
            }}
          >
            {parseInt(p) + 1}
          </Button>
        ))}
        <Button
          className={styles.arrowButtonRight}
          onClick={e => {
            e.preventDefault();
            onNextPage();
          }}
        >
          <ArrowRightIcon style={{ width: "18px", height: "18px" }} />
        </Button>
      </div>
    </div>
  );
};

export type PaginateProps = {
  itemCount: number;
  page: number;
  numPages: number;
  onPageClick: (v: number) => void;
  showPageSize?: boolean;
} & ShowComponentProps;

const Paginate = ({
  itemCount,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
  numPages,
  onPageClick,
  showPageSize = false,
  isLoading = false,
}: PaginateProps) => {
  const [pageInput, setPageInput] = useState("");
  const pages = useMemo(
    () =>
      Array(numPages)
        .fill(0)
        .map((_, i) => i),
    [numPages]
  );
  // startPage will be the start of the pages to be shown for the tables(except 1 and ...)
  // endPage will be the end of the pages to be shown for the tables(except numPages and ...)
  let startPage = 0;
  let endPage = numPages - 1;

  // Calculate start and end page based on current page and numPages
  if (numPages > 8) {
    if (page < 4) {
      startPage = 0;
      endPage = 6;
    } else if (page >= numPages - 4) {
      startPage = numPages - 7;
      endPage = numPages - 1;
    } else {
      startPage = page - 2;
      endPage = page + 2;
    }
  }

  // Display ellipsis for pagination numbers not shown
  const showEllipsisStart = startPage > 1;
  const showEllipsisEnd = endPage < numPages - 2;

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const newPage = parseInt(pageInput) - 1;
      if (!isNaN(newPage) && newPage >= 0 && newPage < numPages) {
        onPageClick(newPage);
      }
      setPageInput("");
    }
  };

  if (!isLoading && (itemCount == null || itemCount === 0)) {
    return <div>No results</div>;
  }

  return (
    <Stack
      className="al-mt-4"
      align="center"
      justify="between"
      aria-label="Pagination"
      role="navigation"
    >
      {showPageSize ? (
        <ShowComponent pageSize={pageSize} onPageSizeChange={onPageSizeChange} />
      ) : null}

      <Stack className="al-flex-1" align="center" justify="end">
        <div>
          Showing{" "}
          <span className="fw-bold">
            {page * pageSize + 1}-{Math.min(itemCount, (page + 1) * pageSize)}
          </span>{" "}
          from <span className="fw-bold">{itemCount}</span> Results | Page{" "}
        </div>
        <Input
          type="text"
          value={pageInput}
          onChange={handlePageInputChange}
          onKeyDown={handlePageInputSubmit}
          className={styles.pageInput}
          placeholder={String(page + 1)}
          aria-label="Go to page number"
        />
        <div className="al-flex al-items-center">
          {page >= 1 && (
            <Button
              variant="secondary"
              className={styles.arrowButtonLeft}
              onClick={e => {
                e.preventDefault();
                onPageClick(page - 1);
              }}
              aria-label="Previous page"
            >
              <ArrowLeftIcon />
            </Button>
          )}
          {showEllipsisStart && (
            <>
              <Button
                variant="ghost"
                color={"white"}
                key={0}
                className="ms-1 me-1"
                onClick={e => {
                  e.preventDefault();
                  onPageClick(0);
                }}
                aria-label={page === 0 ? "Current page" : "Go to page 1"}
              >
                {1}
              </Button>
              <Button
                variant="ghost"
                color={"white"}
                key={"front ..."}
                className="ms-1 me-1"
                onClick={e => {
                  e.preventDefault();
                }}
                style={{ pointerEvents: "none" }}
              >
                ...
              </Button>
            </>
          )}
          {pages.slice(startPage, endPage + 1).map(p => (
            <Button
              variant={page === p ? "default" : "ghost"}
              key={p}
              className="ms-1 me-1"
              onClick={e => {
                e.preventDefault();
                onPageClick(p);
              }}
              aria-label={page === p ? "Current page" : `Go to page ${p + 1}`}
            >
              {p + 1}
            </Button>
          ))}
          {showEllipsisEnd && (
            <>
              <Button
                variant="ghost"
                color={"white"}
                key={"back ..."}
                className="ms-1 me-1"
                onClick={e => {
                  e.preventDefault();
                }}
                style={{ pointerEvents: "none" }}
              >
                ...
              </Button>
              <Button
                variant="ghost"
                color={"white"}
                key={0}
                className="ms-1 me-1"
                onClick={e => {
                  e.preventDefault();
                  onPageClick(numPages - 1);
                }}
                aria-label="Last page"
              >
                {numPages}
              </Button>
            </>
          )}
          {page < numPages - 1 && (
            <Button
              variant="secondary"
              className={styles.arrowButtonRight}
              onClick={e => {
                e.preventDefault();
                onPageClick(page + 1);
              }}
              disabled={page >= numPages - 1}
              aria-label="Next page"
            >
              <ArrowRightIcon />
            </Button>
          )}
        </div>
      </Stack>
    </Stack>
  );
};

export { Paginate, IndefinitePaginate };
